import { ReactComponent as NotAnalysisSvg } from "assets/notAnalysis.svg";
import FathomAnalyze from "components/fathomAnalyze/FathomAnalyze";
import Icon from "components/icon/icon";
import Svg from "components/svg/svg";
import {
  getLegendDisplayOpacity,
  shouldDisplayLegendRule
} from "helpers/legend-display";
import { AppContext, AppAction } from "helpers/context";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { Polygon, Raster } from "../../../helpers/models";
import styles from "./analysis-results.module.scss";
import sortItem from "./sortUtil";
import Expand from "./expand/Expand";
import text from "./text";
import TryAgain from "components/tryAgain/tryAgain";

export default function AnalysisResults() {
  const [context, dispatch] = useContext(AppContext);
  const [t] = useTranslation();

  const handleRetry = () => {
    dispatch({ type: AppAction.setTryAgainType, payload: "analyse" });
  };

  if (!context.analysisData) {
    return (
      <div className="text-center">
        <NotAnalysisSvg className="w-[60px] h-[60px] mx-auto  mb-4" />
        <div style={{ color: "var(--gray-text)" }}>
          {t("single.notAnalysis")}
        </div>
      </div>
    );
  }

  const legendItems = context.analysisData?.legendItems;
  const analysisItems = context.analysisData?.analysisItems;

  sortItem(legendItems);
  sortItem(analysisItems);
  if (!legendItems || !analysisItems) {
    return <TryAgain handleRetry={handleRetry} />;
  }

  if (typeof legendItems === "string" || typeof analysisItems === "string") {
    return (
      <div className="text-center">
        <div className="loading loading-spinner loading-lg"></div>
        <div>{t("single.alalyseLoadingContent")}</div>
      </div>
    );
  }

  const noData = analysisItems.filter(
    (e) =>
      !e.valueLabel ||
      e.valueLabel === "No Data" ||
      ((e.layerId === "DR_Subsidence_France_SwissRe" ||
        e.layerId === "WF_DistToBush_AUS_SwissRe") &&
        (e.valueLabel === "Outside" ||
          e.valueLabel === "No Observation" ||
          e.valueLabel === "Negligible"))
  );

  const legendItemsData = noData.length
    ? legendItems.filter(
      (item) =>
        !noData.some((noDataItem) => noDataItem.layerId === item.layerId)
    )
    : legendItems;

  return (
    <div className="text-xs">
      {legendItemsData &&
        !!legendItemsData.length &&
        legendItemsData.map((e, i) => {
          const item = analysisItems.filter((r) => r.layerId === e.layerId);

          const valueLabel =
            item?.[0]?.valueLabel === "> 500 years"
              ? "> 500 years (US)"
              : item[0]?.valueLabel;
          const intensity = item?.[0]?.intensity;
          let detail: any = item?.[0]?.details;
          let additionalDetails = item?.[0]?.additionalDetails;

          const hailAdditionalDetails = item?.[0]?.hailAdditionalDetails;

          if (hailAdditionalDetails && detail) {
            detail = {
              "Intensity Value": intensity,
              ...detail
            };
          }

          return (
            <div
              key={e.layerId}
              className="border-b border-[#cccccc] border-solid last:border-0 pt-4 pl-2"
            >
              {e.layerId.includes("Fathom") ? (
                <FathomAnalyze
                  key={e.layerId}
                  legendItem={e}
                  analysisItem={item[0]}
                />
              ) : (
                <>
                  <div
                    className="flex items-left items-center font-bold"
                    data-tip={t("layers.item." + e.title)}
                  >
                    <div>{Svg({ icon: e.layerId })()}</div>
                    <div
                      className="truncate max-w-100 ml-2"
                      style={{ fontSize: "13px", fontWeight: "600" }}
                    >
                      {t("layers.item." + e.title)}
                    </div>
                  </div>
                  <div
                    className="flex justify-between gap-2 pb-6"
                    style={{ color: "var(--gray-text)" }}
                  >
                    <div className={styles.legendItem}>
                      <div className="text-start h-[36px] leading-[36px]">
                        {t("single.classification")}
                      </div>
                      {valueLabel === "No Observation" ? (
                        <div id={e.layerId} className={`mb-[3px] pt-[6px]`}>
                          <div
                            style={{ backgroundColor: "grey" }}
                            className={`${styles.lagend} opacity-50 w-full flex-none`}
                          ></div>
                        </div>
                      ) : (
                        (() => {
                          const isVal =
                            valueLabel === "Outside" ||
                            valueLabel === "No Data" ||
                            valueLabel === "No Observation" ||
                            valueLabel === undefined;

                          if (isVal) {
                            return (
                              <div
                                style={{ backgroundColor: "grey" }}
                                className={`w-full h-[5px] border-0 border-gray-500 opacity-50 w-full flex-none  mb-[5px]`}
                              ></div>
                            );
                          }
                          return (
                            <div
                              id={e.layerId}
                              className="flex items-center h-[10px] w-full mb-[2px]"
                            >
                              <div
                                style={{
                                  flex: 1,
                                  border: "1px solid rgba(0,0,0,.12)",
                                  height: "6px",
                                  display: "flex",
                                  alignItems: "center"
                                }}
                              >
                                {e.rules?.map((c, j: number) => {
                                  if (!c.symbolizers?.[0]) {
                                    return null;
                                  }
                                  if (
                                    c.title === "Outside" ||
                                    c.title === "No Data" ||
                                    c.title === "No Observation" ||
                                    c.title === undefined
                                  ) {
                                    return null;
                                  }
                                  const raster = (c.symbolizers[0] as Raster)
                                    ?.raster?.color;
                                  const polygon = (c.symbolizers[0] as Polygon)
                                    ?.polygon?.fill;
                                  const rasterOpacity = (
                                    c.symbolizers[0] as Raster
                                  )?.raster?.opacity;
                                  const polygonOpacity = (
                                    c.symbolizers[0] as Polygon
                                  )?.polygon?.fillOpacity;

                                  const color = raster || polygon;
                                  const opacity =
                                    rasterOpacity ?? polygonOpacity;
                                  const display = shouldDisplayLegendRule(
                                    e.layerId,
                                    c.title,
                                    opacity
                                  )
                                    ? "flex"
                                    : "none";
                                  return (
                                    <div
                                      key={c.title}
                                      style={{
                                        backgroundColor: color,
                                        opacity: getLegendDisplayOpacity(
                                          e.layerId,
                                          c.title,
                                          opacity
                                        ),
                                        display: display
                                      }}
                                      className={`${styles.lagend} ${c.title === valueLabel ? styles.active : ""}`}
                                    ></div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })()
                      )}
                    </div>
                    <div className="flex-1 content-end text-start">
                      {/* <div className="text-start h-[36px] leading-[36px]">
                        {e.layerId === "EQ_Tsunami_SwissRe"
                          ? t("legend.Wave Height")
                          : t("legend.Return Period")}
                      </div> */}
                      <div>{t("legend." + valueLabel)}</div>
                    </div>
                    <div className="max-w-50 flex flex-col items-start">
                      <div className="flex w-full whitespace-nowrap text-ellipsis items-center">
                        {t("single.Intensity")}：
                        <div
                          className={`tooltip tooltip-left ${styles.tooltip}`}
                          data-tip={t("layers.strength")}
                        >
                          <Icon icon="info" type="outlined"></Icon>
                        </div>
                      </div>
                      <div>{intensity === 0 ? 1 : intensity}</div>
                    </div>
                  </div>
                  {detail && (
                    <Expand
                      layerId={e.layerId}
                      detail={detail}
                      additionalDetails={additionalDetails}
                      hailAdditionalDetails={hailAdditionalDetails}
                    />
                  )}
                </>
              )}
            </div>
          );
        })}

      {noData.length > 0 && (
        <div className="m-2">
          {text(
            t,
            noData.map((e) => e.layerId)
          )}
        </div>
      )}
    </div>
  );
}
